/**
 * SccPropertiesView Component
 * 
 * A flexible component for viewing and editing SCC (Smart Cloud Connect) properties.
 * Supports both Edit mode (for existing accounts) and Create mode (for new accounts).
 * 
 * @component
 * 
 * @param {Object} props
 * @param {Object} props.acctDetails - Account details object containing AccountProperties.sccProperties
 * @param {string} [props.mode="edit"] - Component mode: "edit" or "create"
 * @param {Function} [props.onChange] - Callback function for create mode, receives updated properties
 * @param {Object} [props.initialData={}] - Initial SCC properties for create mode
 * 
 * @example
 * // Edit Mode (existing account)
 * <SccPropertiesView 
 *   acctDetails={accountData}
 * />
 * 
 * @example
 * // Create Mode (new account)
 * <SccPropertiesView 
 *   mode="create"
 *   initialData={{ enterprise_id: "", customer: { customer_name: "" } }}
 *   onChange={(updatedProps) => setAccountProperties(updatedProps)}
 *   acctDetails={{}}
 * />
 * 
 * Features:
 * - Toggle between Structured and JSON views
 * - Structured view: organized display with tables for metadata and entitlements
 * - JSON view: interactive editor with add/edit/delete capabilities
 * - Replace JSON: paste complete JSON to replace all properties
 * - Edit mode: saves to API with confirmation
 * - Create mode: passes data to parent via onChange callback
 */

import React, { useState, useEffect } from "react";
import { Card, Button, Space, message, Modal, Input, Descriptions, Tag, Table, Segmented } from "antd";
import _ from "lodash";
import ReactJson from "@microlink/react-json-view";
import { PostRequestResolve } from "utils/FetchHelper";
import URL from "utils/url";
import CustomButton from "../../../../../common/CustomButton/Button";

const { TextArea } = Input;

function SccPropertiesView({ 
    acctDetails, 
    mode = "edit", // "edit" or "create"
    onChange, // callback function for create mode
    initialData = {} // initial data for create mode
}) {
    const acctName = _.get(acctDetails, "Name", "");
    const isCreateMode = mode === "create";
    
    // Use initialData for create mode, or pull from acctDetails for edit mode
    const initialSccProperties = isCreateMode 
        ? initialData 
        : _.get(acctDetails, "AccountProperties.sccProperties", {});
    
    const [sccProperties, setSccProperties] = useState(initialSccProperties);
    const [editMode, setEditMode] = useState(isCreateMode); // Always in edit mode for create
    const [editedProperties, setEditedProperties] = useState(initialSccProperties);
    const [replaceMode, setReplaceMode] = useState(false);
    const [jsonInput, setJsonInput] = useState("");
    const [viewMode, setViewMode] = useState("structured"); // 'structured' or 'json'

    // Notify parent of changes in create mode
    useEffect(() => {
        if (isCreateMode && onChange) {
            onChange(editedProperties);
        }
    }, [editedProperties, isCreateMode, onChange]);

    const handleEdit = () => {
        setEditMode(true);
        setEditedProperties(sccProperties);
    };

    const handleCancel = () => {
        if (isCreateMode) {
            // In create mode, just cancel replace mode
            setReplaceMode(false);
            setEditedProperties(sccProperties);
            setJsonInput("");
        } else {
            // In edit mode, cancel both edit and replace modes
            setEditMode(false);
            setReplaceMode(false);
            setEditedProperties(sccProperties);
            setJsonInput("");
        }
    };

    const handleReplaceJson = () => {
        setReplaceMode(true);
        setEditMode(false);
        setJsonInput(JSON.stringify(sccProperties, null, 2));
    };

    const handleJsonInputChange = (e) => {
        setJsonInput(e.target.value);
    };

    const handleApplyReplacedJson = () => {
        try {
            const parsedJson = JSON.parse(jsonInput);
            setEditedProperties(parsedJson);
            setReplaceMode(false);
            setEditMode(true);
            message.success("JSON replaced successfully. You can now save the changes.");
        } catch (error) {
            message.error("Invalid JSON format. Please check your input.");
        }
    };

    const handleSave = () => {
        // In create mode, just update local state (parent will handle via onChange)
        if (isCreateMode) {
            setSccProperties(editedProperties);
            setEditMode(true); // Keep in edit mode for create
            message.success("SCC Properties updated");
            return;
        }

        // In edit mode, save to API
        Modal.confirm({
            title: "Save SCC Properties",
            content: "Are you sure you want to save the updated SCC properties?",
            okText: "Yes",
            cancelText: "No",
            onOk: () => {
                const payload = {
                    acctName: acctName,
                    sccProperties: editedProperties,
                };
                PostRequestResolve(URL.updateSccAccountProperties, null, payload)
                    .then(() => {
                        message.success("SCC Properties updated successfully");
                        setSccProperties(editedProperties);
                        setEditMode(false);
                    })
                    .catch((err) => {
                        message.error("Failed to update SCC Properties: " + _.get(err, "message", "Unknown error"));
                        console.error("Error updating SCC Properties:", err);
                    });
            }
        });
    };

    const renderStructuredView = () => {
        const canEdit = isCreateMode || editMode;
        const properties = canEdit ? editedProperties : sccProperties;
        
        // Helper function to check if a value is not empty
        const hasValue = (value) => {
            return value !== undefined && value !== null && value !== "";
        };

        // Build description items only for properties with values
        const descriptionItems = [];
        
        if (hasValue(_.get(properties, "enterprise_id"))) {
            descriptionItems.push(
                <Descriptions.Item key="enterprise_id" label="Enterprise ID">
                    {_.get(properties, "enterprise_id")}
                </Descriptions.Item>
            );
        }
        
        if (hasValue(_.get(properties, "product_instance_id"))) {
            descriptionItems.push(
                <Descriptions.Item key="product_instance_id" label="Product Instance ID">
                    {_.get(properties, "product_instance_id")}
                </Descriptions.Item>
            );
        }
        
        if (hasValue(_.get(properties, "customer.customer_name"))) {
            descriptionItems.push(
                <Descriptions.Item key="customer_name" label="Customer Name">
                    {_.get(properties, "customer.customer_name")}
                </Descriptions.Item>
            );
        }
        
        if (hasValue(_.get(properties, "customer.initial_admin"))) {
            descriptionItems.push(
                <Descriptions.Item key="initial_admin" label="Initial Admin">
                    {_.get(properties, "customer.initial_admin")}
                </Descriptions.Item>
            );
        }
        
        if (hasValue(_.get(properties, "processing_meta.requestID"))) {
            descriptionItems.push(
                <Descriptions.Item key="requestID" label="Request ID">
                    {_.get(properties, "processing_meta.requestID")}
                </Descriptions.Item>
            );
        }
        
        if (hasValue(_.get(properties, "processing_meta.callback_url"))) {
            descriptionItems.push(
                <Descriptions.Item key="callback_url" label="Callback URL">
                    {_.get(properties, "processing_meta.callback_url")}
                </Descriptions.Item>
            );
        }
        
        return (
            <div>
                {descriptionItems.length > 0 && (
                    <Descriptions bordered size="small" column={1} style={{ marginBottom: 16 }}>
                        {descriptionItems}
                    </Descriptions>
                )}

                {/* Schemas */}
                {_.size(_.get(properties, "schemas", [])) > 0 && (
                    <Card size="small" title="Schemas" style={{ marginBottom: 16 }}>
                        <Space wrap>
                            {_.map(_.get(properties, "schemas", []), (schema, idx) => (
                                <Tag key={idx}>{schema}</Tag>
                            ))}
                        </Space>
                    </Card>
                )}

                {/* Provisioning Metadata */}
                {_.size(_.get(properties, "provisioning_metadata", [])) > 0 && (
                    <Card size="small" title="Provisioning Metadata" style={{ marginBottom: 16 }}>
                        <Table
                            dataSource={_.get(properties, "provisioning_metadata", [])}
                            rowKey={(record, idx) => idx}
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: "Name",
                                    dataIndex: "name",
                                    key: "name",
                                },
                                {
                                    title: "Value",
                                    dataIndex: "value",
                                    key: "value",
                                }
                            ]}
                        />
                    </Card>
                )}

                {/* Entitlements */}
                {_.size(_.get(properties, "entitlements", [])) > 0 && (
                    <Card size="small" title="Entitlements" style={{ marginBottom: 16 }}>
                        <Table
                            dataSource={_.get(properties, "entitlements", [])}
                            rowKey={(record) => record.name}
                            pagination={false}
                            size="small"
                            columns={[
                                {
                                    title: "Name",
                                    dataIndex: "name",
                                    key: "name",
                                },
                                {
                                    title: "Value",
                                    dataIndex: "value",
                                    key: "value",
                                },
                                {
                                    title: "Quantity Value",
                                    dataIndex: ["quantity", "value"],
                                    key: "quantity_value",
                                },
                                {
                                    title: "Unit",
                                    dataIndex: ["quantity", "unit"],
                                    key: "unit",
                                },
                                {
                                    title: "Enforce Quantity",
                                    dataIndex: "enforce_quantity",
                                    key: "enforce_quantity",
                                    render: (text) => (
                                        <Tag color={text ? "green" : "red"}>
                                            {text ? "Yes" : "No"}
                                        </Tag>
                                    )
                                }
                            ]}
                        />
                    </Card>
                )}

                {/* Show message if no properties are configured */}
                {descriptionItems.length === 0 && 
                 _.size(_.get(properties, "schemas", [])) === 0 && 
                 _.size(_.get(properties, "provisioning_metadata", [])) === 0 && 
                 _.size(_.get(properties, "entitlements", [])) === 0 && (
                    <div style={{ 
                        padding: '40px 20px', 
                        textAlign: 'center', 
                        color: '#999',
                        backgroundColor: '#fafafa',
                        borderRadius: '4px'
                    }}>
                        <p style={{ margin: 0, fontSize: '14px' }}>
                            No SCC properties configured yet.
                            {isCreateMode && " Use JSON view or Replace JSON to add properties."}
                        </p>
                    </div>
                )}
            </div>
        );
    };

    const renderJsonView = () => {
        const canEdit = isCreateMode || editMode;
        const dataToShow = canEdit ? editedProperties : sccProperties;
        const isEmpty = Object.keys(dataToShow).length === 0;
        
        if (isEmpty && !canEdit) {
            return (
                <div style={{ 
                    padding: '40px 20px', 
                    textAlign: 'center', 
                    color: '#999',
                    backgroundColor: '#fafafa',
                    borderRadius: '4px'
                }}>
                    <p style={{ margin: 0, fontSize: '14px' }}>
                        No SCC properties configured yet.
                    </p>
                </div>
            );
        }
        
        return (
            <ReactJson
                src={dataToShow}
                name={false}
                collapsed={1}
                enableClipboard={canEdit}
                onEdit={canEdit ? (edit => setEditedProperties(edit.updated_src)) : false}
                onAdd={canEdit ? (add => setEditedProperties(add.updated_src)) : false}
                onDelete={canEdit ? (del => setEditedProperties(del.updated_src)) : false}
            />
        );
    };

    return (
        <Card
            size="small"
            title="SCC Properties"
            extra={
                <Space>
                    {!replaceMode && (
                        <Segmented
                            options={[
                                { label: "Structured", value: "structured" },
                                { label: "JSON", value: "json" }
                            ]}
                            value={viewMode}
                            onChange={setViewMode}
                            size="small"
                        />
                    )}
                    {isCreateMode ? (
                        // Create mode - always editable, only show Replace JSON option
                        replaceMode ? (
                            <>
                                <Button size="small" onClick={handleCancel}>Cancel</Button>
                                <Button type="primary" size="small" onClick={handleApplyReplacedJson}>Apply JSON</Button>
                            </>
                        ) : (
                            <Button size="small" onClick={handleReplaceJson}>Replace JSON</Button>
                        )
                    ) : (
                        // Edit mode - show full Edit/Save controls
                        editMode ? (
                            <>
                                <Button size="small" onClick={handleCancel}>Cancel</Button>
                                <Button type="primary" size="small" onClick={handleSave}>Save</Button>
                            </>
                        ) : replaceMode ? (
                            <>
                                <Button size="small" onClick={handleCancel}>Cancel</Button>
                                <Button type="primary" size="small" onClick={handleApplyReplacedJson}>Apply JSON</Button>
                            </>
                        ) : (
                            <>
                                <CustomButton size="small" onClick={handleEdit} enableOnlyForAdmin>Edit</CustomButton>
                                <CustomButton size="small" onClick={handleReplaceJson} enableOnlyForAdmin>Replace JSON</CustomButton>
                            </>
                        )
                    )}
                </Space>
            }
        >
            {replaceMode ? (
                <div>
                    <p style={{ marginBottom: 8, color: '#666' }}>
                        Paste your JSON here to replace the entire SCC Properties object:
                    </p>
                    <TextArea
                        value={jsonInput}
                        onChange={handleJsonInputChange}
                        placeholder="Paste your JSON here..."
                        rows={15}
                        style={{ fontFamily: 'monospace', fontSize: '12px' }}
                    />
                </div>
            ) : (
                viewMode === "structured" ? renderStructuredView() : renderJsonView()
            )}
        </Card>
    );
}

export default SccPropertiesView;

